import type React from 'react';
import { useState } from 'react';

import { useAppState, useDispatch } from 'state';
import { setLanguage } from 'actions/language';
import useConfig from 'app/config';

import { useQuery, useMutation } from '@apollo/client';

import LanguagePickerComponent from 'app/ui/components/molecules/picker/language';

import Loading from 'app/ui/components/molecules/loading-with-overlay';

import { LanguagePickerWrapper as LANGUAGES } from 'app/ui/components/molecules/picker/language/languages.gql';
import { UpdatePreferredLanguage as UPDATE_LANGUAGE } from 'app/ui/components/molecules/picker/language/update-language.gql';

import type {
  Language,
  LanguagePickerWrapperQuery,
  LanguagePickerWrapperQueryVariables,
  UpdatePreferredLanguageMutation,
  UpdatePreferredLanguageMutationVariables,
} from 'app/types/graphql';
import { trackEvent } from 'src/app/utils/analytics/amplitude/tracker';

const emptyLanguagePicker = (
  <LanguagePickerComponent languages={[]} preferredLanguage={null} onChange={() => undefined} />
);

interface LanguagePickerProps {
  preferredLanguage: Language;
}

const UserLanguagePickerWrapper: React.FC<LanguagePickerProps> = ({ preferredLanguage }) => {
  const dispatch = useDispatch();
  const { config } = useConfig();
  const thaiEnabled = config.REACT_APP_THAI_ENABLED;

  const [isRefreshing, setIsRefreshing] = useState(false);

  const userId = useAppState(({ auth }) => auth.userId) as number;
  const [userLanguage, setUserLanguage] = useState<Language>(preferredLanguage);

  const { data, loading } = useQuery<LanguagePickerWrapperQuery, LanguagePickerWrapperQueryVariables>(LANGUAGES);

  const [updatePreferredLanguage] = useMutation<
    UpdatePreferredLanguageMutation,
    UpdatePreferredLanguageMutationVariables
  >(UPDATE_LANGUAGE);

  if (loading) {
    return emptyLanguagePicker;
  }

  const languages =
    (thaiEnabled ? data?.allLanguages : data?.allLanguages?.filter(({ code }) => code.toLowerCase() !== 'th')) || [];

  const handleLanguageChange = async (language: Language) => {
    setIsRefreshing(true);
    setUserLanguage(language);

    trackEvent('language_changed', {
      from_lang: userLanguage.code,
      to_lang: language.code,
    });

    if (userId) {
      await updatePreferredLanguage({ variables: { userId, code: language.code } });
    }

    dispatch(setLanguage(language));

    window.location.reload();
  };

  return (
    <>
      {isRefreshing && <Loading />}
      <LanguagePickerComponent languages={languages} preferredLanguage={userLanguage} onChange={handleLanguageChange} />
    </>
  );
};

const LanguagePicker: React.FC = () => {
  const preferredLanguage = useAppState(({ language }) => language.preferredLanguage) as Language;

  return <UserLanguagePickerWrapper preferredLanguage={preferredLanguage} />;
};

export default LanguagePicker;
